import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ZONES, WORLD, BANK } from '../config/zones';
import { useStore } from '../store';
import { mulberry32 } from './rng';
import { shared } from './shared';

// ---- LEFT BANK (Progressive): glowing vine networks + hive structures ----
function Hive({ x, highlight }) {
  const grpRef = useRef();
  const glow = useRef();
  const z = BANK.progressive.z;

  useFrame((state) => {
    if (glow.current) {
      const night = shared.env.night;
      const base = (highlight ? 1.6 : 0.7) + night * 1.5;
      glow.current.intensity = base + Math.sin(state.clock.elapsedTime * 2 + x) * 0.3;
      // Green by day -> electric blue bioluminescence at night
      glow.current.color.setRGB(0.18 + night * 0.04, 0.9 - night * 0.5, 0.66 + night * 0.34);
    }
  });

  const cells = useMemo(() => {
    const rand = mulberry32(Math.floor(x) + 900);
    return Array.from({ length: 7 }).map(() => ({
      p: [(rand() - 0.5) * 10, 4 + rand() * 8, (rand() - 0.5) * 10],
      s: 1 + rand() * 1.6,
    }));
  }, [x]);

  const vines = useMemo(() => {
    const rand = mulberry32(Math.floor(x) + 50);
    return Array.from({ length: 10 }).map(() => ({
      p: [(rand() - 0.5) * 16, 6 + rand() * 6, (rand() - 0.5) * 16],
      h: 6 + rand() * 8,
      r: rand() * Math.PI,
    }));
  }, [x]);

  return (
    <group ref={grpRef} position={[x, 0, z]}>
      <pointLight ref={glow} color="#2fe6a8" distance={40} intensity={0.7} position={[0, 8, 0]} />
      {/* Central hive spire */}
      <mesh position={[0, 6, 0]} castShadow>
        <cylinderGeometry args={[2.2, 3.4, 12, 6]} />
        <meshStandardMaterial color="#0d3b2e" emissive="#1f8f5f" emissiveIntensity={highlight ? 0.9 : 0.4} roughness={0.6} flatShading />
      </mesh>
      {/* Hive cells */}
      {cells.map((c, i) => (
        <mesh key={i} position={c.p} castShadow>
          <icosahedronGeometry args={[c.s, 0]} />
          <meshStandardMaterial color="#2fe6a8" emissive="#2fe6a8" emissiveIntensity={highlight ? 1.4 : 0.7} roughness={0.3} flatShading />
        </mesh>
      ))}
      {/* Dynamic glowing vines */}
      {vines.map((v, i) => (
        <mesh key={`v${i}`} position={v.p} rotation={[v.r, 0, v.r * 0.5]}>
          <cylinderGeometry args={[0.12, 0.12, v.h, 5]} />
          <meshStandardMaterial color="#39ffc0" emissive="#2fe6a8" emissiveIntensity={highlight ? 1.2 : 0.6} />
        </mesh>
      ))}
    </group>
  );
}

// ---- RIGHT BANK (Traditional): stone fortress + perimeter fence ----
function Fortress({ x, highlight }) {
  const z = BANK.traditional.z;
  const light = useRef();
  useFrame(() => {
    if (light.current) {
      const night = shared.env.night;
      light.current.intensity = night * 2.0 + (highlight ? 0.8 : 0);
    }
  });
  const towerMat = (
    <meshStandardMaterial color="#8d8578" emissive={highlight ? '#c9a24a' : '#000'} emissiveIntensity={highlight ? 0.35 : 0} roughness={0.9} flatShading />
  );

  const fence = useMemo(() => {
    const posts = [];
    for (let i = -18; i <= 18; i += 3) {
      posts.push([i, 0, -14]);
      posts.push([i, 0, 14]);
    }
    for (let j = -14; j <= 14; j += 3) {
      posts.push([-18, 0, j]);
      posts.push([18, 0, j]);
    }
    return posts;
  }, []);

  return (
    <group position={[x, 0, z]}>
      {/* Red perimeter bioluminescence at night */}
      <pointLight ref={light} color="#ff4d3a" distance={40} intensity={0} position={[0, 10, 0]} />
      {/* Keep */}
      <mesh position={[0, 7, 0]} castShadow>
        <boxGeometry args={[10, 14, 10]} />
        {towerMat}
      </mesh>
      {/* Battlement */}
      <mesh position={[0, 14.5, 0]} castShadow>
        <boxGeometry args={[11, 1.5, 11]} />
        <meshStandardMaterial color="#736c60" roughness={0.9} flatShading />
      </mesh>
      {/* Corner towers */}
      {[[-6, -6], [6, -6], [-6, 6], [6, 6]].map(([cx, cz], i) => (
        <mesh key={i} position={[cx, 5, cz]} castShadow>
          <cylinderGeometry args={[2, 2.4, 12, 6]} />
          {towerMat}
        </mesh>
      ))}
      {/* Perimeter fence posts */}
      {fence.map((p, i) => (
        <mesh key={`f${i}`} position={[p[0], 1.2, p[2]]}>
          <boxGeometry args={[0.4, 2.4, 0.4]} />
          <meshStandardMaterial color="#5a4632" roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

export default function BankStructures() {
  const currentZone = useStore((s) => s.currentZone);
  const bankFocus = useStore((s) => s.bankFocus);
  const tourActive = useStore((s) => s.tourActive);

  return (
    <group>
      {ZONES.map((zone) => {
        const isCurrent = tourActive && zone.index === currentZone;
        return (
          <group key={zone.id}>
            <Hive x={zone.x} highlight={isCurrent && bankFocus === 'progressive'} />
            <Fortress x={zone.x} highlight={isCurrent && bankFocus === 'traditional'} />
          </group>
        );
      })}
    </group>
  );
}
